import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Heading from "../common/Heading";
import './Careers.css';
import Header from '../common/header/Header'   

// Sample data - this would typically come from an API or database
const careersData = [
  {
    id: 1,
    title: "Agribusiness Officer",
    company: "Farm Radio Holdings Limited (FHL)",
    location: "Lilongwe",
    type: "Full-time",
    duration: "1-year contract, renewable",
    applicationDeadline: "2025-10-03",
    postedDate: "2025-09-15",
    description: "FHL is looking for dynamic Agribusiness Officer to roll out the implementation, coordination, and sustainability of Mlimi Youth Fund.",
    requirements: [
      "Bachelor's degree in Agribusiness, Agriculture Economics, Agronomy, or related field",
      "At least 5-8 years of experience in agribusiness development or agricultural finance",
      "Experience working with youth, NGOs, or donor-funded agricultural projects"
    ],
    salary: "Competitive package",
    category: "Agriculture"
  }
];

const Careers = () => {
  const [careers, setCareers] = useState(careersData);
  const [filteredCareers, setFilteredCareers] = useState(careersData);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Function to check if a job is active based on application deadline
  const isJobActive = (deadline) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    return deadlineDate >= today;
  };

  // Function to format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Function to calculate days remaining
  const getDaysRemaining = (deadline) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const timeDiff = deadlineDate.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return daysDiff;
  };

  // Filter careers based on category and status
  useEffect(() => {
    let filtered = careers;

    if (filterCategory !== 'all') {
      filtered = filtered.filter(career => career.category.toLowerCase() === filterCategory);
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(career => {
        const active = isJobActive(career.applicationDeadline);
        return filterStatus === 'active' ? active : !active;
      });
    }

    setFilteredCareers(filtered);
  }, [filterCategory, filterStatus, careers]);

  return (
    <>
    <Header/>
      <section className='careers-hero'>
        <div className='container'>
          <div className='careers-hero-content'>
            <h1>Opportunities & Careers</h1>
            <p>Join our mission to transform agriculture and development through innovative multimedia solutions</p>
          </div>
        </div>
      </section>

      <section className='careers-main'>
        <div className='container'>
          <Heading title='Current Opportunities' subtitle='Discover exciting career opportunities with Farm Radio Trust' />
          
          {/* Filters */}
          <div className='careers-filters'>
            <div className='filter-group'>
              <label>Category:</label>
              <select 
                value={filterCategory} 
                onChange={(e) => setFilterCategory(e.target.value)}
                className='filter-select'
              >
                <option value='all'>All Categories</option>
                <option value='agriculture'>Agriculture</option>
                <option value='technology'>Technology</option>
                <option value='media'>Media</option>
                <option value='administration'>Administration</option>
              </select>
            </div>
            
            <div className='filter-group'>
              <label>Status:</label>
              <select 
                value={filterStatus} 
                onChange={(e) => setFilterStatus(e.target.value)}
                className='filter-select'
              >
                <option value='all'>All Positions</option>
                <option value='active'>Active</option>
                <option value='inactive'>Closed</option>
              </select>
            </div>
          </div>

          {/* Career Cards */}
          <div className='careers-grid'>
            {filteredCareers.length > 0 ? (
              filteredCareers.map((career) => {
                const active = isJobActive(career.applicationDeadline);
                const daysRemaining = getDaysRemaining(career.applicationDeadline);
                
                return (
                  <div key={career.id} className={`career-card ${!active ? 'inactive' : ''}`}>
                    <div className='career-card-header'>
                      <div className='career-status'>
                        <span className={`status-badge ${active ? 'active' : 'inactive'}`}>
                          {active ? 'Active' : 'Closed'}
                        </span>
                        <span className='career-category'>{career.category}</span>
                      </div>
                      {active && daysRemaining > 0 && (
                        <div className='days-remaining'>
                          {daysRemaining} days left
                        </div>
                      )}
                    </div>

                    <div className='career-card-content'>
                      <h3 className='career-title'>{career.title}</h3>
                      <div className='career-meta'>
                        <div className='meta-item'>
                          <i className='fas fa-building'></i>
                          <span>{career.company}</span>
                        </div>
                        <div className='meta-item'>
                          <i className='fas fa-map-marker-alt'></i>
                          <span>{career.location}</span>
                        </div>
                        <div className='meta-item'>
                          <i className='fas fa-clock'></i>
                          <span>{career.type}</span>
                        </div>
                        <div className='meta-item'>
                          <i className='fas fa-calendar'></i>
                          <span>{career.duration}</span>
                        </div>
                      </div>

                      <p className='career-description'>{career.description}</p>

                      <div className='career-requirements'>
                        <h4>Key Requirements:</h4>
                        <ul>
                          {career.requirements.slice(0, 2).map((req, index) => (
                            <li key={index}>{req}</li>
                          ))}
                        </ul>
                      </div>

                      <div className='career-footer'>
                        <div className='deadline-info'>
                          <span className='deadline-label'>Application Deadline:</span>
                          <span className='deadline-date'>{formatDate(career.applicationDeadline)}</span>
                        </div>
                        
                        <Link 
                          to={`/careers/${career.id}`} 
                          className={`btn-apply ${!active ? 'disabled' : ''}`}
                          onClick={(e) => !active && e.preventDefault()}
                        >
                          {active ? 'View Details & Apply' : 'Application Closed'}
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className='no-careers'>
                <i className='fas fa-briefcase'></i>
                <h3>No opportunities found</h3>
                <p>Try adjusting your filters or check back later for new opportunities.</p>
              </div>
            )}
          </div>

          {/* Call to Action */}
          <div className='careers-cta'>
            <div className='cta-content'>
              <h2>Looking for a career opportunity?</h2>
              <p> Send us your CV and we'll keep you in mind for future opportunities.</p>
              <a href='mailto:info@farmradiomw.org' className='btn-cta'>
                Send Your CV
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Careers;
